import { applyStaticImageColors, getImageLightPreviewLayout, mapImageBitmapToKeyColors } from './frame_mapper'
import type { ImageLightFitMode } from './types'
import { setData } from '@/service/driver/data'
import { dynamicLightFrameIntervalMs, dynamicLightSendFrameRate } from '@libs/music/frame_rate'
import { backgroundScheduler } from '@libs/utils/background_scheduler'
import { registerDynamicImagePauseController } from './dynamic_image_pause'
import { imageLightHidWriter } from './hid_writer'
import { dynamicLightFrameWriter } from '@/service/dynamic_light/frame_writer'
import { DeviceCapability } from '@/service/device_capability'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { imageLightPersistence, type ImageLightSourceKind, type PersistedImageLight } from './image_light_persistence'
import { DynamicImagePlayback, type DynamicImageFrame } from './dynamic_image_playback'
import { decodeDynamicImageFrames } from './dynamic_image_decode'
import { resolveImageLightFileKind, validateImageLightDynamicFile, validateImageLightStaticFile, isImageLightStaticFile, type ImageLightFileKind, type ImageLightFileValidationError } from './image_light_file'
import { applyPersistedImageLightOptions, createImageLightOptions, imageLightState, resetImageLightPlacement, revokeImageLightPreviewUrl, setImageLightPreviewFile } from './image_light_state'

export { imageLightDynamicMaxSizeMB, imageLightMaxFrameCount, imageLightStaticMaxSizeMB } from './image_light_file'
export { imageLightState, type ImageLightApplyStatus } from './image_light_state'

export const imageLightSendIntervalMs = dynamicLightFrameIntervalMs
export const imageLightSendFrameRate = dynamicLightSendFrameRate
export const imageLightMinScale = 0.2
export const imageLightMaxScale = 3

let lastStaticFile: File | null = null
let lastDynamicFile: File | null = null
let dynamicFrames: DynamicImageFrame[] = []
let dynamicStateApplied = false
const dynamicImageResumeDelayMs = 500
const maxConsecutiveDynamicWriteFailures = 20
let currentPersistenceScope = ''
let restoringPersistedDynamic = false
// Every file selection, apply request, reset, or configuration switch starts a
// new operation. Async decoding may not be abortable, so stale operations are
// prevented from mutating state when they eventually resolve.
let operationToken = 0

const ensureImageLightUploadSupported = () => {
  if (DeviceCapability.isImageLightUploadSupported()) {
    return true
  }

  setImageLightError('image_light_unsupported_device', 'current device does not support image light upload')
  return false
}

const resetStatusLater = () => {
  backgroundScheduler.setTimer(() => {
    if (imageLightState.status === 'success') {
      imageLightState.status = 'idle'
    }
  }, 1200)
}

const beginOperation = () => ++operationToken
const isCurrentOperation = (token: number) => token === operationToken

const setImageLightError = (errorKey: string, context: string, detail?: unknown, token?: number) => {
  if (token !== undefined && !isCurrentOperation(token)) return
  imageLightState.errorKey = errorKey
  imageLightState.status = 'error'
  console.warn('[image-light]', context, { errorKey, detail })
}

const setFileValidationError = (error: ImageLightFileValidationError, token?: number) => {
  setImageLightError(error.errorKey, error.context, error.detail, token)
}

const persistImage = async (file: File) => {
  try {
    await imageLightPersistence.save(currentPersistenceScope, file, createImageLightOptions())
  } catch (error) {
    console.warn('[image-light] persist image failed', error)
  }
}

const readPersistedImage = async () => {
  try {
    return await imageLightPersistence.load(currentPersistenceScope)
  } catch (error) {
    console.warn('[image-light] read persisted image failed', error)
    return null
  }
}

const removePersistedDynamicImage = async () => {
  try {
    await imageLightPersistence.remove(currentPersistenceScope)
  } catch (error) {
    console.warn('[image-light] remove persisted image failed', error)
  }
}

const removePersistedDynamicImagesByScopePrefix = async (scopePrefix: string) => {
  try {
    await imageLightPersistence.removeByScopePrefix(scopePrefix)
  } catch (error) {
    console.warn('[image-light] remove persisted images by scope prefix failed', error)
  }
}

const getCurrentImageLightSource = () => imageLightPersistence.getSource(currentPersistenceScope)
const syncCurrentImageLightSourceState = () => {
  imageLightState.persistedSource = getCurrentImageLightSource() || 'none'
  return imageLightState.persistedSource
}
const setCurrentImageLightSource = (source: ImageLightSourceKind) => {
  imageLightPersistence.setSource(currentPersistenceScope, source)
  imageLightState.persistedSource = source
}
const removeCurrentImageLightSource = () => {
  imageLightPersistence.removeSource(currentPersistenceScope)
  imageLightState.persistedSource = 'none'
}
const removeImageLightSourcesByScopePrefix = (scopePrefix: string) => imageLightPersistence.removeSourcesByScopePrefix(scopePrefix)
const createFileFromPersistedImage = (persisted: PersistedImageLight) => imageLightPersistence.toFile(persisted)
const persistCustomColorSnapshot = (colors: Record<number, number>) => imageLightPersistence.setCustomColors(currentPersistenceScope, colors)
const readCustomColorSnapshot = () => imageLightPersistence.getCustomColors(currentPersistenceScope)
const removeCustomColorSnapshot = () => imageLightPersistence.removeCustomColors(currentPersistenceScope)
const removeCustomColorSnapshotsByScopePrefix = (scopePrefix: string) => imageLightPersistence.removeCustomColorsByScopePrefix(scopePrefix)

const resetImageLightSession = () => {
  revokeImageLightPreviewUrl()
  lastStaticFile = null
  lastDynamicFile = null
  dynamicFrames = []
  dynamicPlayback.clearFrames()
  imageLightState.status = 'idle'
  imageLightState.fileName = ''
  imageLightState.errorKey = ''
  imageLightState.activeKind = 'none'
  imageLightState.activeSource = 'color'
  imageLightState.persistedSource = getCurrentImageLightSource() || 'none'
  imageLightState.hasApplied = false
  imageLightState.frameCount = 0
  imageLightState.currentFrame = 0
}

const sendDynamicFrame = async (frame: number[]) => {
  return await dynamicLightFrameWriter.sendFrame(frame).catch(() => false)
}

const hasVisibleColor = (colors: Record<number, number>) => {
  return Object.values(colors).some((color) => color !== 0)
}

const hasVisibleFrame = (frame: number[]) => {
  return frame.some((value) => value !== 0)
}

const isBrightnessOff = () => imageLightState.brightness <= 0

const ensureDynamicState = async (run: boolean, options: { keepCustomSelection?: boolean } = {}) => {
  const data = setData.getData()
  data.LightLastIsCustom = run || Boolean(options.keepCustomSelection)
  data.ColorsDynamic.Run = run
  data.ColorsDynamic.FullColor = true
  data.ColorsDynamic.Mode = data.ColorsDynamic.Mode || 0
  data.ColorsDynamic.Color = data.ColorsDynamic.Color || 0
  data.ColorsDynamic.IsEEvision = data.ColorsDynamic.IsEEvision || false

  const ok = await dynamicLightFrameWriter.setDynamicState(data.ColorsDynamic, { requestAccess: run }).catch(() => false)
  if (ok) {
    dynamicStateApplied = run
  }
  return ok
}

const decodeAnimationFrames = async (file: File, token: number) => {
  return await decodeDynamicImageFrames(file, {
    frameIntervalMs: imageLightSendIntervalMs,
    getOptions: createImageLightOptions,
    isCurrentOperation: () => isCurrentOperation(token),
    setError: (errorKey, context, detail) => setImageLightError(errorKey, context, detail, token),
  })
}

const dynamicPlayback = new DynamicImagePlayback({
  frameIntervalMs: imageLightSendIntervalMs,
  resumeDelayMs: dynamicImageResumeDelayMs,
  maxConsecutiveFailures: maxConsecutiveDynamicWriteFailures,
  getLoop: () => imageLightState.loop,
  sendFrame: sendDynamicFrame,
  onFrame: (index) => (imageLightState.currentFrame = index),
  onFinished: () => void imageLight.stopDynamicImage(),
  onWriteFailure: () => void imageLight.stopDynamicImage('image_light_dynamic_write_failed'),
})

const prepareImage = (file: File, kind: Exclude<ImageLightFileKind, 'unsupported'>) => {
  const isDynamic = kind === 'dynamic'
  const validationError = isDynamic ? validateImageLightDynamicFile(file) : validateImageLightStaticFile(file)
  if (validationError) {
    setFileValidationError(validationError)
    return false
  }

  lastStaticFile = isDynamic ? null : file
  lastDynamicFile = isDynamic ? file : null
  dynamicFrames = []
  imageLightState.fileName = file.name
  imageLightState.errorKey = ''
  imageLightState.status = 'idle'
  imageLightState.activeKind = kind
  imageLightState.activeSource = 'image'
  imageLightState.hasApplied = false
  imageLightState.frameCount = 0
  imageLightState.currentFrame = 0
  imageLightState.fitMode = 'contain'
  imageLightState.brightness = 100
  resetImageLightPlacement()
  setImageLightPreviewFile(file)
  return true
}

export const imageLight = {
  isCurrentImageSource() {
    const source = imageLightState.persistedSource !== 'none' ? imageLightState.persistedSource : getCurrentImageLightSource()
    return source === 'static' || source === 'dynamic'
  },

  isImageLightActive() {
    return imageLightState.activeSource === 'image' || this.isCurrentImageSource()
  },

  setPersistenceScope(scope: string) {
    if (currentPersistenceScope && currentPersistenceScope !== scope) {
      beginOperation()
      void this.stopDynamicImage('', { syncDynamicState: false })
      resetImageLightSession()
    }
    currentPersistenceScope = scope
    syncCurrentImageLightSourceState()

    if (setData.getData().LightLastIsCustom && this.isCurrentImageSource() && imageLightState.activeKind === 'none') {
      void this.restorePersistedImage({ autoPlay: false })
    }
  },

  async selectFile(file: File) {
    const token = beginOperation()
    if (!ensureImageLightUploadSupported()) return false

    const kind = await resolveImageLightFileKind(file)
    if (!isCurrentOperation(token)) return false
    if (kind === 'unsupported') {
      setImageLightError('image_light_static_type_error', 'unsupported image type during file selection', { name: file.name, type: file.type }, token)
      return false
    }
    if (kind === 'dynamic' && !specialCheckCurrent(speciaBehiver.SupportMusicRhythmSupported)) {
      setImageLightError('image_light_static_type_error', 'dynamic image is not supported by current device', { name: file.name, type: file.type }, token)
      return false
    }

    if (imageLightState.activeSource !== 'image') {
      persistCustomColorSnapshot({ ...(setData.getData().LightCustom || {}) })
    }

    await this.stopDynamicImage('', { cancelPending: false })
    if (!isCurrentOperation(token)) return false
    return prepareImage(file, kind)
  },

  async applyStaticImage(file: File = lastStaticFile as File) {
    if (!file) return false
    if (!ensureImageLightUploadSupported()) return false
    setData.cancelPendingCustomColorWrite()
    const token = beginOperation()
    const wasImageActive = imageLightState.activeSource === 'image'
    await this.stopDynamicImage('', { cancelPending: false })
    if (!isCurrentOperation(token)) return false
    imageLightState.status = 'loading'
    imageLightState.fileName = file.name
    imageLightState.errorKey = ''
    imageLightState.activeKind = 'static'
    imageLightState.activeSource = 'image'
    lastStaticFile = file
    setImageLightPreviewFile(file)

    try {
      const validationError = validateImageLightStaticFile(file)
      if (validationError) {
        setFileValidationError(validationError)
        return false
      }

      if (isBrightnessOff()) {
        setImageLightError('image_light_brightness_off', 'image light brightness is zero', { name: file.name, type: file.type, brightness: imageLightState.brightness })
        return false
      }

      const layout = getImageLightPreviewLayout()
      if (layout.keys.length === 0) {
        setImageLightError('image_light_layout_unavailable', 'image light key layout is empty', { name: file.name, type: file.type })
        return false
      }

      const writerReady = await imageLightHidWriter.ensureConnected({ requestAccess: true })
      if (!isCurrentOperation(token)) return false
      if (!writerReady) {
        setImageLightError('image_light_hid_authorize_required', 'HID light device is not connected or authorized')
        return false
      }

      const bitmap = await createImageBitmap(file)
      try {
        if (!isCurrentOperation(token)) return false
        const colors = mapImageBitmapToKeyColors(bitmap, createImageLightOptions())
        if (!hasVisibleColor(colors)) {
          setImageLightError('image_light_no_visible_color', 'static image sampled no visible LED colors', { name: file.name, type: file.type })
          return false
        }
        if (!wasImageActive) {
          persistCustomColorSnapshot({ ...(setData.getData().LightCustom || {}) })
        }
        await applyStaticImageColors(colors)
        if (!isCurrentOperation(token)) return false
      } finally {
        bitmap.close()
      }

      imageLightState.status = 'success'
      imageLightState.hasApplied = true
      setCurrentImageLightSource('static')
      void persistImage(file)
      resetStatusLater()
      return true
    } catch (error) {
      console.error('apply static image light failed', error)
      setImageLightError(error instanceof Error && error.message === 'image_light_hid_authorize_required' ? 'image_light_hid_authorize_required' : 'image_light_decode_failed', 'apply static image light failed', error, token)
      return false
    }
  },

  async applyDynamicImage(file: File = lastDynamicFile as File) {
    if (!file) return false
    if (!ensureImageLightUploadSupported()) return false
    setData.cancelPendingCustomColorWrite()
    const token = beginOperation()
    await this.stopDynamicImage('', { cancelPending: false })
    if (!isCurrentOperation(token)) return false
    imageLightState.status = 'loading'
    imageLightState.fileName = file.name
    imageLightState.errorKey = ''
    imageLightState.activeKind = 'dynamic'
    imageLightState.activeSource = 'image'
    lastDynamicFile = file
    setImageLightPreviewFile(file)

    try {
      const validationError = validateImageLightDynamicFile(file)
      if (validationError) {
        setFileValidationError(validationError)
        return false
      }

      if (isBrightnessOff()) {
        setImageLightError('image_light_brightness_off', 'image light brightness is zero', { name: file.name, type: file.type, brightness: imageLightState.brightness })
        return false
      }

      const layout = getImageLightPreviewLayout()
      if (layout.keys.length === 0) {
        setImageLightError('image_light_layout_unavailable', 'image light key layout is empty', { name: file.name, type: file.type })
        return false
      }

      const frames = await decodeAnimationFrames(file, token)
      if (!isCurrentOperation(token)) return false
      if (!frames || frames.length === 0) {
        setImageLightError(imageLightState.errorKey || 'image_light_decode_failed', 'animated image decoded no frames', { name: file.name, type: file.type })
        return false
      }

      if (!frames.some(({ frame }) => hasVisibleFrame(frame))) {
        setImageLightError('image_light_no_visible_color', 'animated image sampled no visible LED colors', { name: file.name, type: file.type, frameCount: frames.length })
        return false
      }

      dynamicFrames = frames
      dynamicPlayback.setFrames(frames)
      imageLightState.frameCount = frames.length
      const dynamicStateEnabled = await ensureDynamicState(true)
      if (!isCurrentOperation(token)) return false
      if (!dynamicStateEnabled) {
        setImageLightError('image_light_dynamic_write_failed', 'failed to enable dynamic image light state')
        return false
      }
      imageLightState.status = 'playing'
      imageLightState.hasApplied = true
      dynamicPlayback.start()
      setCurrentImageLightSource('dynamic')
      if (!restoringPersistedDynamic) {
        void persistImage(file)
      }
      return true
    } catch (error) {
      console.error('apply dynamic image light failed', error)
      setImageLightError(error instanceof Error && error.message === 'image_light_hid_authorize_required' ? 'image_light_hid_authorize_required' : 'image_light_decode_failed', 'apply dynamic image light failed', error, token)
      return false
    }
  },

  async reapplyCurrent() {
    if (imageLightState.activeKind === 'static' && lastStaticFile) {
      return await this.applyStaticImage(lastStaticFile)
    }

    if (imageLightState.activeKind === 'dynamic' && lastDynamicFile) {
      return await this.applyDynamicImage(lastDynamicFile)
    }

    return false
  },

  resetPlacement() {
    resetImageLightPlacement()
    // Resetting placement only changes the draft. The user must explicitly
    // apply again before the reset position is written to the keyboard.
    imageLightState.hasApplied = false
    if (imageLightState.status === 'success') {
      imageLightState.status = 'idle'
    }
  },

  setFitMode(fitMode: ImageLightFitMode) {
    if (imageLightState.fitMode === fitMode) return
    imageLightState.fitMode = fitMode
    this.resetPlacement()
  },

  setPlacement(offsetX: number, offsetY: number) {
    if (imageLightState.offsetX === offsetX && imageLightState.offsetY === offsetY) return
    imageLightState.offsetX = offsetX
    imageLightState.offsetY = offsetY
    this.markDraftChanged()
  },

  setScale(scale: number) {
    const nextScale = Math.min(imageLightMaxScale, Math.max(imageLightMinScale, scale))
    if (imageLightState.scale === nextScale) return
    imageLightState.scale = nextScale
    this.markDraftChanged()
  },

  adjustScale(delta: number) {
    this.setScale(Number((imageLightState.scale + delta).toFixed(2)))
  },

  setBrightness(brightness: number) {
    const nextBrightness = Math.min(100, Math.max(0, brightness))
    if (imageLightState.brightness === nextBrightness) return
    imageLightState.brightness = nextBrightness
    this.markDraftChanged()
  },

  markDraftChanged() {
    imageLightState.hasApplied = false
    if (imageLightState.status === 'success') imageLightState.status = 'idle'
  },

  async restorePersistedDynamicImage(options: { autoPlay?: boolean } = {}) {
    if (!DeviceCapability.isImageLightUploadSupported() || !specialCheckCurrent(speciaBehiver.SupportMusicRhythmSupported)) {
      await this.stopDynamicImage('', { syncDynamicState: false })
      resetImageLightSession()
      return false
    }

    if (getCurrentImageLightSource() === 'color') {
      return false
    }

    const persisted = await readPersistedImage()
    if (!persisted) {
      return false
    }

    applyPersistedImageLightOptions(persisted.options)
    const file = createFileFromPersistedImage(persisted)

    if (options.autoPlay) {
      restoringPersistedDynamic = true
      try {
        return await this.applyDynamicImage(file)
      } finally {
        restoringPersistedDynamic = false
      }
    }

    await this.stopDynamicImage('', { syncDynamicState: false })
    lastStaticFile = null
    lastDynamicFile = file
    dynamicFrames = []
    imageLightState.fileName = file.name
    imageLightState.errorKey = ''
    imageLightState.status = 'idle'
    imageLightState.activeKind = 'dynamic'
    imageLightState.activeSource = 'image'
    imageLightState.hasApplied = true
    imageLightState.frameCount = 0
    imageLightState.currentFrame = 0
    setImageLightPreviewFile(file)
    return true
  },

  async restorePersistedStaticImage() {
    if (!DeviceCapability.isImageLightUploadSupported() || getCurrentImageLightSource() !== 'static') {
      return false
    }

    const persisted = await readPersistedImage()
    if (!persisted || !isImageLightStaticFile(createFileFromPersistedImage(persisted))) {
      return false
    }

    applyPersistedImageLightOptions(persisted.options)
    const file = createFileFromPersistedImage(persisted)
    await this.stopDynamicImage('', { syncDynamicState: false })
    lastDynamicFile = null
    dynamicFrames = []
    lastStaticFile = file
    imageLightState.fileName = file.name
    imageLightState.errorKey = ''
    imageLightState.status = 'idle'
    imageLightState.activeKind = 'static'
    imageLightState.activeSource = 'image'
    imageLightState.hasApplied = true
    imageLightState.frameCount = 0
    imageLightState.currentFrame = 0
    setImageLightPreviewFile(file)
    return true
  },

  async restorePersistedImage(options: { autoPlay?: boolean } = {}) {
    if (getCurrentImageLightSource() === 'static') {
      return await this.restorePersistedStaticImage()
    }

    return await this.restorePersistedDynamicImage(options)
  },

  async syncWithCurrentConfig(isDynamicRunning: boolean) {
    if (!DeviceCapability.isImageLightUploadSupported()) {
      await this.stopDynamicImage('', { syncDynamicState: false })
      resetImageLightSession()
      return false
    }

    if (getCurrentImageLightSource() === 'static') {
      if (imageLightState.activeKind === 'static') return true
      return await this.restorePersistedStaticImage()
    }

    if (!specialCheckCurrent(speciaBehiver.SupportMusicRhythmSupported)) {
      await this.stopDynamicImage('', { syncDynamicState: false })
      resetImageLightSession()
      return false
    }

    if (!isDynamicRunning) {
      await this.stopDynamicImage('', { syncDynamicState: false })
      if (await this.restorePersistedDynamicImage({ autoPlay: false })) {
        return true
      }
      resetImageLightSession()
      return false
    }

    if (imageLightState.status === 'playing') {
      return true
    }

    if (lastDynamicFile) {
      return await this.applyDynamicImage(lastDynamicFile)
    }

    return await this.restorePersistedDynamicImage({ autoPlay: true })
  },

  async removeCurrentImage() {
    beginOperation()
    await this.stopDynamicImage('', { keepCustomSelection: false })
    resetImageLightSession()
    await removePersistedDynamicImage()
    removeCurrentImageLightSource()
    removeCustomColorSnapshot()
    imageLightState.activeSource = 'color'
    try {
      await applyStaticImageColors({})
    } catch (error) {
      console.warn('[image-light] failed to clear colors on remove', error)
    }
  },

  async clearPersistedImageForScope(scope: string) {
    if (!scope) return
    await imageLightPersistence.remove(scope)
    imageLightPersistence.removeSource(scope)
    imageLightPersistence.removeCustomColors(scope)
  },

  async clearPersistedImagesForCurrentDevice() {
    const scopePrefix = currentPersistenceScope ? `${currentPersistenceScope.split(':')[0]}:` : ''
    await this.stopDynamicImage('', { syncDynamicState: false })
    resetImageLightSession()
    if (!scopePrefix) {
      await removePersistedDynamicImage()
      removeCurrentImageLightSource()
      return
    }
    await removePersistedDynamicImagesByScopePrefix(scopePrefix)
    removeImageLightSourcesByScopePrefix(scopePrefix)
    removeCustomColorSnapshotsByScopePrefix(scopePrefix)
  },

  saveCustomColorSnapshot(colors: Record<number, number>) {
    persistCustomColorSnapshot(colors)
  },

  async restoreCustomColorSnapshot() {
    const colors = readCustomColorSnapshot()
    if (!colors) return false

    const current = setData.getData()
    current.LightLastIsCustom = true
    await setData.setKeysColor(colors, { ...(current.LightCustom || {}) })
    return true
  },

  async markCustomColorActive() {
    setCurrentImageLightSource('color')
    imageLightState.activeSource = 'color'
    await this.stopDynamicImage('', { keepCustomSelection: true })
    await this.restoreCustomColorSnapshot()
  },

  async deactivateImageLight(options: { keepCustomSelection?: boolean } = {}) {
    setCurrentImageLightSource('color')
    imageLightState.activeSource = 'color'
    await this.stopDynamicImage('', { keepCustomSelection: options.keepCustomSelection })
  },

  async markImageLightActive() {
    if (imageLightState.activeKind === 'none') {
      const restored = await this.restorePersistedImage({ autoPlay: false })
      if (!restored) return false
      if (imageLightState.hasApplied) {
        imageLightState.activeSource = 'color'
      }
    }

    // A newly selected image is only a draft until the user explicitly applies
    // it. Selecting the image card must never write a draft as a side effect.
    if (!imageLightState.hasApplied) {
      imageLightState.activeSource = 'image'
      return true
    }

    if (imageLightState.activeKind === 'none') return false
    setCurrentImageLightSource(imageLightState.activeKind)
    return await this.reapplyCurrent()
  },

  async stopDynamicImage(errorKey: string = '', options: { syncDynamicState?: boolean; keepCustomSelection?: boolean; cancelPending?: boolean } = {}) {
    if (options.cancelPending !== false) beginOperation()
    const syncDynamicState = options.syncDynamicState ?? true
    const shouldSyncDynamicState = syncDynamicState && dynamicStateApplied
    dynamicPlayback.stop()

    if (imageLightState.status === 'playing') {
      imageLightState.status = errorKey ? 'error' : 'idle'
    }

    if (errorKey) {
      imageLightState.errorKey = errorKey
      console.warn('[image-light] dynamic image stopped with error', { errorKey })
    }

    if (shouldSyncDynamicState) {
      await ensureDynamicState(false, { keepCustomSelection: options.keepCustomSelection })
    }
  },

  async pauseDynamicImage() {
    if (imageLightState.status !== 'playing' || !dynamicPlayback.hasFrames) {
      return false
    }
    return dynamicPlayback.pause()
  },

  resumeDynamicImage() {
    if (imageLightState.status === 'playing') dynamicPlayback.resume()
  },

  dispose() {
    this.stopDynamicImage()
    resetImageLightSession()
  },
}

registerDynamicImagePauseController({
  pause: () => imageLight.pauseDynamicImage(),
  resume: () => imageLight.resumeDynamicImage(),
})
